import { ModButton, ModIconButton } from '@moderneinc/design-system-components';
import {
  borderRadius,
  iconSize,
  semanticColors,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, Dialog, Divider, dialogClasses, Stack } from '@mui/material';
import { bindDialog, usePopupState } from 'material-ui-popup-state/hooks';
import { useRouter } from 'next/router';
import React, {
  type ChangeEvent,
  type FunctionComponent,
  type KeyboardEventHandler,
  useEffect,
  useRef
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import type { RecipeDescriptor } from '../../../__generated__/apollo-hooks';
import {
  DOHERTY_THRESHOLD,
  SEARCH_INPUT_ID,
  SEARCH_OVERLAY_TOP_SPACING
} from '../../../constants/general';
import { SEARCH_BAR_PLACEHOLDER } from '../../../constants/messages';
import { focusFirstFocusable } from '../../../helpers/focus.helper';
import {
  recipeDetailsUrl,
  searchForRecipeUrl
} from '../../../helpers/link.helper';
import { globalSearchEvent } from '../../../hooks/use-app-event.hooks';
import { CloseIcon, SearchIcon } from '../../../icons/icons';
import {
  selectActiveStepId,
  useTourEngineStore
} from '../../../stores/tour-engine.store';
import { useUserPreferenceStore } from '../../../stores/user-preference.store';
import { HighlightedSearchInput } from '../../global-search/highlighted-search-input.component';
import { PreviousSearchQueries } from '../../global-search/previous-search-queries.component';
import { SearchModalFooter } from '../../global-search/search-modal-footer.component';
import { SearchRecipes } from '../../global-search/search-recipes.component';
import { SearchTrigger } from '../../global-search/search-trigger.component';

export const GlobalSearchInput: FunctionComponent = () => {
  const previousSearchQueries =
    useUserPreferenceStore((state) => state.previousSearchQueries) ?? [];
  const update = useUserPreferenceStore((state) => state.update);
  const router = useRouter();
  const contentRef = useRef<HTMLDivElement>(null);
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'global-search'
  });

  globalSearchEvent.useListener(popupState.open);

  const methods = useForm<{
    query: string;
  }>();

  const queryValue = methods.watch('query');

  // Single source of truth: the query lives in React Hook Form and the
  // debounced search term is DERIVED from it (same shape as
  // organizations.table.component.tsx). Clearing must apply instantly, so only
  // non-empty terms are debounced — the `=== ''` short-circuit, not `.cancel()`,
  // is what makes the reset immediate (useDebounce schedules during render).
  const trimmedQuery = (queryValue ?? '').trim();
  const [debouncedQuery] = useDebounce(trimmedQuery, DOHERTY_THRESHOLD);
  const searchTerm = trimmedQuery === '' ? '' : debouncedQuery;

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.trim() !== '') {
      methods.setValue('query', e.target.value);
    } else {
      methods.resetField('query');
    }
  };

  const handleRedirect = (input: string | RecipeDescriptor): void => {
    if (typeof input === 'string') {
      router.push(searchForRecipeUrl(input.trim()));
    } else if (input?.id) {
      router.push(recipeDetailsUrl({ id: input.id }));
    }
  };

  const updateRecentSearchQueries = (query: string) => {
    if (query) {
      update({
        previousSearchQueries: Array.from(
          new Set([query.trim(), ...previousSearchQueries])
        ).slice(0, 5)
      });
    }
  };

  const resetState = () => {
    methods.resetField('query');
    popupState.close();
  };

  const handleClearQuery = () => {
    methods.resetField('query');
    document.getElementById(SEARCH_INPUT_ID)?.focus();
  };

  const handleFormSubmit = (data: { query: string }) => {
    updateRecentSearchQueries(data.query);
    handleRedirect(data.query || '');
    resetState();
  };

  const activeTourStepId = useTourEngineStore(selectActiveStepId);
  const advanceTour = useTourEngineStore((state) => state.advance);
  const isOpenSearchStep = activeTourStepId === 'open-search';
  const isTypeRecipeStep = activeTourStepId === 'type-recipe';
  const isChooseRecipeStep = activeTourStepId === 'choose-recipe';

  // While the guided tour is driving the search dialog, Enter must not submit
  // the query — that navigates away and closes the dialog, skipping the
  // choose-recipe step and derailing the tour. The user advances by typing,
  // then clicking the spotlighted recipe.
  const isTourDrivingSearch =
    isOpenSearchStep || isTypeRecipeStep || isChooseRecipeStep;

  const handleKeyDown: KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      resetState();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isTourDrivingSearch) {
        return;
      }
      handleFormSubmit({
        query: (e.target as HTMLInputElement).value
      });
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusFirstFocusable(contentRef.current);
    }
  };

  const showRecipeResults = searchTerm !== '';
  const showPreviousTerms = searchTerm === '';

  // The "open-search" tour step advances once the search dialog is open.
  useEffect(() => {
    if (isOpenSearchStep && popupState.isOpen) {
      advanceTour();
    }
  }, [isOpenSearchStep, advanceTour, popupState.isOpen]);

  // The "type-recipe" tour step advances once the user has typed the target query.
  useEffect(() => {
    if (
      isTypeRecipeStep &&
      (queryValue ?? '').trim().toLowerCase().startsWith('common static')
    ) {
      advanceTour();
    }
  }, [isTypeRecipeStep, advanceTour, queryValue]);

  // RHF's onBlur re-writes `query` from the DOM input as focus leaves during
  // the dialog close, resurrecting a just-reset value on reopen. Exclude it so
  // our reset paths (Escape/Enter/Clear) are the only writers of this field.
  const { onBlur: _queryFieldBlur, ...queryFieldRegistration } =
    methods.register('query');

  return (
    <React.Fragment>
      <SearchTrigger onClick={popupState.open} />
      {/* Raw Dialog instead of ModModal: this search modal has a non-standard
          layout (input + scrollable results + key-hint footer) that doesn't
          fit ModModal's header/content structure. */}
      <Dialog
        {...bindDialog(popupState)}
        onClose={resetState}
        aria-label="Search recipes"
        maxWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: semanticColors.color.surface.scrim
            }
          }
        }}
        sx={{
          [`& .${dialogClasses.paper}`]: {
            position: 'absolute',
            top: (theme) => theme.spacing(SEARCH_OVERLAY_TOP_SPACING),
            width: '100%',
            maxWidth: 800,
            height: '80vh',
            maxHeight: 600,
            mx: 2,
            borderRadius: `${borderRadius.card}px`,
            border: `1px solid ${semanticColors.color.border.strong}`,
            overflow: 'hidden'
          }
        }}
      >
        <FormProvider {...methods}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleFormSubmit({ query: queryValue || '' });
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 1.5,
                  alignItems: 'center',
                  px: 2.5,
                  // Shorter header to match the mock (~15px vertical).
                  py: 1.5
                }}
              >
                <SearchIcon
                  size={iconSize.md}
                  style={{
                    color: semanticColors.color.icon.utility,
                    flexShrink: 0
                  }}
                />
                <HighlightedSearchInput
                  {...queryFieldRegistration}
                  id={SEARCH_INPUT_ID}
                  value={queryValue || ''}
                  placeholder={SEARCH_BAR_PLACEHOLDER}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  autoFocus
                  fullWidth
                  disableChrome
                  // Mock query text is 14px, flush under the search icon. These
                  // drive BOTH the input's text and the highlight overlay, so
                  // the caret and glyphs stay aligned.
                  textFontSize={typography.fontSize.sm}
                  textPaddingLeft={0}
                  inputProps={{ 'data-tour': 'type-recipe' }}
                  sx={{
                    border: 'none',
                    borderRadius: 0,
                    background: 'none',
                    padding: 0,
                    '&:focus-within': {
                      border: 'none',
                      boxShadow: 'none',
                      background: 'none'
                    },
                    '& input': {
                      padding: 0
                    },
                    '& input::placeholder': {
                      color: semanticColors.color.text.inputPlaceholder,
                      opacity: 1,
                      WebkitTextFillColor:
                        semanticColors.color.text.inputPlaceholder
                    }
                  }}
                />
                {queryValue && (
                  <ModButton
                    variant="tertiary"
                    size="small"
                    onClick={handleClearQuery}
                    // Link blue, not the navy tertiary default (mock).
                    sx={{
                      flexShrink: 0,
                      color: semanticColors.color.text.link
                    }}
                  >
                    Clear
                  </ModButton>
                )}
                <ModIconButton
                  size="small"
                  onClick={resetState}
                  sx={{ flexShrink: 0 }}
                  aria-label="Close search"
                >
                  <CloseIcon size={'1.25rem'} />
                </ModIconButton>
              </Stack>
            </Box>

            <Divider />

            <Box
              ref={contentRef}
              sx={{
                flex: 1,
                overflow: 'auto'
              }}
            >
              {showPreviousTerms && (
                <PreviousSearchQueries
                  onSelect={(query) => {
                    updateRecentSearchQueries(query);
                    handleRedirect(query);
                    resetState();
                  }}
                />
              )}
              {showRecipeResults && (
                <SearchRecipes
                  searchTerm={searchTerm}
                  resetModal={resetState}
                  onRecipeSelect={() => updateRecentSearchQueries(searchTerm)}
                />
              )}
            </Box>

            <SearchModalFooter />
          </Box>
        </FormProvider>
      </Dialog>
    </React.Fragment>
  );
};
